/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BEM } from "@adapptio/lib-components";
import React, { FC, useEffect, useRef, useState } from "react";

export enum TABBED_CONTAINER_TYPE {
	SWITCH = "switch",
	SLIDE = "slide"
}

/**
 * Tab Item Object
 */
export interface ITabbedContainerItem {
	/** Tab ID */
	id: string;
	/** Render functions */
	render: () => JSX.Element;
	/** If to render item when it's not active */
	renderWhenHidden?: boolean;
}

type TTabbedContainerItemEffect = "slide-to-left" | "slide-to-right" | "slide-from-left" | "slide-from-right";

interface ITabbedContainerItemProps {
	item: ITabbedContainerItem;
	effect: TTabbedContainerItemEffect;
	selected: boolean;
}

const TabbedContainerItem: FC<ITabbedContainerItemProps> = ({ item, effect, selected }) => {
	const [ isVisible, setIsVisible ] = useState(selected);
	const lastEffect = useRef(effect);

	useEffect(() => {
		let t = null;

		if (selected && !isVisible) {
			setIsVisible(true);
		} else if (!selected && isVisible) {
			t = setTimeout(() => setIsVisible(false), 500);
		}

		return () => {
			if (t) {
				clearTimeout(t);
			}
		};
	}, [ selected, isVisible ]);

	if (effect !== null && effect !== lastEffect.current) {
		lastEffect.current = effect;
	}

	const baseClass = BEM("edt-tabbed-container", "item", {
		"is-selected": selected,
		"is-visible": isVisible,
		effect: lastEffect.current || "none"
	});

	baseClass.add("m-tab-cnt-id-" + item.id);

	return (
		<div className={baseClass.toClassName()}>
			{item.renderWhenHidden !== false || (item.renderWhenHidden === false && isVisible)
				? item.render()
				: null}
		</div>
	);
};

/**
 * Component props
 */
export interface ITabbedContainerProps {
	items: ITabbedContainerItem[];
	selectedId: string;
	style?: TABBED_CONTAINER_TYPE;
	className?: string;
}

/**
 * TabbedContainer component
 */
export const TabbedContainer: FC<ITabbedContainerProps> = ({ items, selectedId, style, className }) => {
	const [ state, setState ] = useState<{
		currentId: string;
		currentIndex: number;
		prevId: string;
		prevIndex: number;
	}>(null);

	useEffect(() => {
		let currentIndex = 0;

		for (let i = 0; i < items.length; i++) {
			if (items[i].id === selectedId) {
				currentIndex = i;
			}
		}

		setState((state) => {
			return {
				currentId: items[currentIndex]?.id,
				currentIndex: currentIndex,
				prevId: state ? state.currentId : items[currentIndex]?.id,
				prevIndex: state ? state.currentIndex : currentIndex
			};
		});
	}, [ items, selectedId ]);

	const baseClass = BEM("edt-tabbed-container", null, {
		style: style || TABBED_CONTAINER_TYPE.SWITCH
	});

	if (className) {
		baseClass.add(className);
	}

	return (
		<div className={baseClass.toClassName()}>
			<div className={baseClass.element("container").toClassName()}>
				{items.map((item) => {
					let effect: TTabbedContainerItemEffect = null;

					if (state && state.currentId === item.id) {
						if (state.currentIndex < state.prevIndex) {
							effect = "slide-from-left";
						} else if (state.currentIndex > state.prevIndex) {
							effect = "slide-from-right";
						}
					} else if (state && state.prevId === item.id) {
						if (state.prevIndex < state.currentIndex) {
							effect = "slide-to-left";
						} else if (state.prevIndex > state.currentIndex) {
							effect = "slide-to-right";
						}
					}

					return (
						<TabbedContainerItem
							key={item.id}
							item={item}
							effect={effect}
							selected={item.id === selectedId}
						/>
					);
				})}
			</div>
		</div>
	);
};
